import axios from 'axios';

export const dynamic = 'force-dynamic';

export interface LessonModule {
  id: number;
  name: string;
  lessons: Lesson[];
}
[];

export interface PDF {
  id: number;
  name: string;
  size: number;
  created_at: string;
}

export interface Lesson {
  id: number;
  name: string;
  completed: boolean;
  content: string;
  language: string;
  pdfs: PDF[];
}

export interface LmsSettings {
  content_name_plural: string;
  content_name_singular: string;
  next_lesson_button_text: string;
  prev_lesson_button_text: string;
  progress_bar_enabled: boolean;
  start_button_text: string;
}

export interface UserCourse {
  id: number;
  name: string;
  title?: string;
  description: string;
  image: string;
  progressPercentage: number;
  progress: {
    [id: Lesson['id']]: {
      date_completed: string;
      language: string;
      status: string;
    };
  };
  numLessons: number;
  completedLessons: number;
  modules: LessonModule[];
  lms_settings: LmsSettings;
}

export interface CustomerInfo {
  email: string;
  stripe_customer_id?: string;
}

export interface AccountInfo {
  name: string;
  slug: string;
  logo_url: string;
  favicon_url: string;
  website: string;
  support_email_address: string;
  default_customer_portal: boolean;
  customer_portal_url?: string | null;
  lms_settings: {
    header_enabled: boolean;
    header_image: string;
    header_text_color: string;
    header_background_color: string;
    header_headline: string;
    header_text: string;
    theme_headline_font: string;
    theme_body_font: string;
    theme_accent_color: string;
    sidebar_enabled: boolean;
    sidebar_text: string;
    footer_text: string;
    footer_text_color: string;
    footer_background_color: string;
  };
  language_code: string;
}

export interface CustomerPortal {
  error: boolean | null;
  url: string;
}

// Temporary solution using traditional Next.js API routes until we can fully convert LMS to server-side components
// https://nextjs.org/docs/pages/building-your-application/routing/api-routes
const getBaseUrl = () =>
  `${document.location.protocol}//${document.location.host}`;

export const getLesson = (
  lessonId: Lesson['id'],
  courseId: UserCourse['id']
): Promise<Lesson> =>
  axios
    .get<{ data: Lesson }>(
      `${getBaseUrl()}/api/courses/${courseId}/lesson/${lessonId}`
    )
    .then((data) => data.data.data);

export const getCourses = (): Promise<UserCourse[]> =>
  axios
    .get<{ data: UserCourse[] }>(`${getBaseUrl()}/api/courses/all`)
    .then((data) => data.data.data);

export const getCourse = (courseId: UserCourse['id']): Promise<UserCourse> =>
  axios.get(`${getBaseUrl()}/api/courses/${courseId}`).then((data) => {
    const course = data.data.data;
    course.modules.forEach((module) =>
      module.lessons.forEach((lesson) => {
        lesson.completed = course.progress[lesson.id]?.status === 'completed';
      })
    );
    return course;
  });

export const updateLessonStatus = (
  lessonId: Lesson['id'],
  courseId: UserCourse['id'],
  status: 1 | 0
): Promise<UserCourse> =>
  axios
    .post(`${getBaseUrl()}/api/courses/${courseId}/lesson/${lessonId}/status`, {
      status,
    })
    .then((data) => data.data.data);

export const getCustomerInfo = (accountName: string): Promise<CustomerInfo> =>
  axios
    .get(`${getBaseUrl()}/api/customer-info?account_name=${accountName}`)
    .then((data) => data.data);

export const getAccountInfo = (): Promise<AccountInfo> =>
  axios.get(`${getBaseUrl()}/api/account-info`).then((data) => ({
    ...data.data.data,
    lms_settings: data.data.data?.lms_settings ?? defaultLMSSettings,
  }));

export const getCustomerPortalUrl = (
  returnUrl: string
): Promise<CustomerPortal> =>
  axios
    .get(`${getBaseUrl()}/api/customer-portal?returnUrl=${returnUrl}`)
    .then((data) => data.data);

const defaultLMSSettings: AccountInfo['lms_settings'] = {
  header_enabled: false,
  header_image: '',
  header_text_color: '#2D2D2D',
  header_background_color: '#EBEBEB',
  header_headline: '',
  header_text: '',
  theme_headline_font: 'Lato',
  theme_body_font: 'Roboto',
  theme_accent_color: '#6471E3',
  sidebar_enabled: false,
  sidebar_text: '',
  footer_text: '',
  footer_text_color: '#979797',
  footer_background_color: '#F7F8F9',
};

export const getPDF = (
  courseId: string,
  lessonId: number,
  pdfId: number
): Promise<string> =>
  axios
    .get(
      `${getBaseUrl()}/api/courses/${courseId}/lesson/${lessonId}/pdf/${pdfId}`
    )
    .then((res) => res.data?.url);

export const getPDFBlob = (url: string): Promise<Blob> =>
  fetch(url).then((res) => res.blob());
